import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Avatar,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CompanyVeiwController from "../../redux/actions/companyApiActions";
import { IMAGE_URL } from "../../services/api";
import FormDrawer from "../../components/layout/FormDrawer";
import { primaryButtonSx } from "../../components/auth/authTheme";

const validationSchema = Yup.object({
  company_name: Yup.string()
    .trim()
    .required("Company name is required"),
  email: Yup.string()
    .trim()
    .required("Email is required")
    .email("Please enter a valid email address"),
  employee_strength: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? undefined : value
    )
    .typeError("Employee strength must be a number")
    .required("Employee strength is required")
    .integer("Employee strength must be a whole number")
    .min(1, "Employee strength must be at least 1"),
  location: Yup.string().trim(),
  description: Yup.string().trim(),
});

function CompanyFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const { addCompany, updateCompany } = CompanyVeiwController();
  const { isLoding } = useSelector((state) => state.company);

  const isEdit = Boolean(initialData?.id);

  const [initialValues, setInitialValues] = useState({
    company_name: "",
    email: "",
    location: "",
    employee_strength: "",
    description: "",
    company_logo: "",
  });

  useEffect(() => {
    if (show) {
      const base = {
        company_name: initialData?.company_name || "",
        email: initialData?.email || "",
        location: initialData?.location || "",
        employee_strength: initialData?.employee_strength || "",
        description: initialData?.description || "",
        company_logo: "",
      };

      setInitialValues(base);

      if (initialData?.company_logo) {
        const fullUrl = `${IMAGE_URL}${initialData.company_logo}`;
        convertImageUrlToBase64(fullUrl).then((base64) => {
          setInitialValues((prev) => ({
            ...prev,
            company_logo: base64,
          }));
        });
      }
    }
  }, [show, initialData]);

  const convertImageUrlToBase64 = async (imageUrl) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const handleImageToBase64 = (file, setFieldValue) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setFieldValue("company_logo", reader.result);
    };
    if (file) reader.readAsDataURL(file);
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = {
        ...values,
        employee_strength: Number(values.employee_strength),
      };

      const action = isEdit
        ? updateCompany(initialData.id, payload)
        : addCompany(payload);

      await dispatch(action);
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        submitForm,
        setFieldValue,
        handleChange,
        handleBlur,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
        <Form id="company-form" style={{ display: "contents" }}>
          <FormDrawer
            open={show}
            onClose={handleClose}
            title={isEdit ? "Edit Company" : "Create Company"}
            subtitle={
              isEdit
                ? "Update organization details"
                : "Add a new organization to the system"
            }
            footer={
              <>
                <Button onClick={handleClose} color="inherit" disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                  onClick={() => submitForm()}
                  sx={{
                    ...primaryButtonSx,
                    minWidth: 120,
                    "&.Mui-disabled": {
                      bgcolor: isSaving ? "#FF6A00" : undefined,
                      color: isSaving ? "#fff" : undefined,
                      opacity: isSaving ? 0.85 : undefined,
                    },
                  }}
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                >
                  {isSaving
                    ? isEdit
                      ? "Updating..."
                      : "Creating..."
                    : isEdit
                      ? "Update"
                      : "Create"}
                </Button>
              </>
            }
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company Name"
                  name="company_name"
                  value={values.company_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  required
                  error={touched.company_name && Boolean(errors.company_name)}
                  helperText={touched.company_name && errors.company_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  required
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Location"
                  name="location"
                  value={values.location}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Employee Strength"
                  name="employee_strength"
                  type="number"
                  value={values.employee_strength}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  required
                  inputProps={{ min: 1, step: 1 }}
                  error={
                    touched.employee_strength &&
                    Boolean(errors.employee_strength)
                  }
                  helperText={
                    touched.employee_strength && errors.employee_strength
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>

              {values.company_logo && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Current Logo
                  </Typography>
                  <Avatar
                    src={values.company_logo}
                    alt="Company Logo"
                    variant="rounded"
                    sx={{ width: 100, height: 100 }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth>
                  Upload Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) =>
                      handleImageToBase64(e.target.files[0], setFieldValue)
                    }
                  />
                </Button>
              </Grid>
            </Grid>
          </FormDrawer>
        </Form>
        );
      }}
    </Formik>
  );
}

export default CompanyFormModal;
